import { Inject, Injectable } from '@angular/core';

import { MSAL_GUARD_CONFIG, MsalGuardConfiguration, MsalService } from '@azure/msal-angular';
import { InteractionRequiredAuthError, PopupRequest, SilentRequest } from '@azure/msal-browser';

import { ChatAgentService } from './chat-agent.service';

/**
 * Provee el access token con el que se llama al agente Bryant.
 *
 * El agente valida un token emitido para su propia app registration, no el de
 * la app web, por eso se pide un token aparte con el id del agente elegido
 * en lugar de reutilizar el de la sesión.
 */
@Injectable({ providedIn: 'root' })
export class AgentAuthService {

    /**
     * Scope del agente. Bryant está registrado como bot de Teams, así que su
     * App ID URI es `api://botid-{id}` y el permiso delegado que expone es
     * `access_as_user`. Si en Entra cambia el App ID URI o el nombre del scope,
     * este es el único punto que hay que tocar.
     *
     * Se arma en cada pedido: en desarrollo se puede cambiar de agente y cada
     * despliegue tiene su propia app registration.
     */
    private get scopes(): string[] {
        return [`api://botid-${this._agentService.current.id}/access_as_user`];
    }

    /**
     * Constructor
     * @param {MsalService} _msalService
     * @param {MsalGuardConfiguration} msalGuardConfig
     * @param {ChatAgentService} _agentService Agente contra el que habla el chat
     */
    constructor(private _msalService: MsalService,
        private _agentService: ChatAgentService,
        @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration) { }

    /**
     * Obtiene el access token del agente Bryant.
     *
     * Primero lo intenta en silencio, que es lo normal cuando el usuario ya
     * consintió la api. Si Entra pide interacción (falta consentimiento, MFA o
     * la sesión ya no sirve) cae a un popup.
     *
     * @returns {Promise<string>} Devuelve el access token del agente
     */
    async getAccessToken(): Promise<string> {
        const silentRequest = { ...this.msalGuardConfig.authRequest } as SilentRequest;
        silentRequest.scopes = this.scopes;

        // Sin cuenta activa msal no sabe a nombre de quién pedir el token
        silentRequest.account = silentRequest.account
            ?? this._msalService.instance.getActiveAccount()
            ?? this._msalService.instance.getAllAccounts()[0];

        try {
            const result = await this._msalService.acquireTokenSilent(silentRequest).toPromise();

            return result.accessToken;
        } catch (error) {
            if (!(error instanceof InteractionRequiredAuthError)) {
                throw error;
            }

            const popupRequest = { ...this.msalGuardConfig.authRequest, scopes: this.scopes } as PopupRequest;
            const result = await this._msalService.acquireTokenPopup(popupRequest).toPromise();

            return result.accessToken;
        }
    }
}
